import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtom } from "jotai";
import { isNil } from "lodash";
import { useCallback } from "react";
import { useFragment } from "react-relay";

import { setFiltersFromStorage } from "@/components/AgendaFilters/utils/setFiltersFromStorage";
import {
  facilityToScheduleOption,
  ScheduleOption,
} from "@/components/ScheduleSwitcher";
import { ScheduleSwitcher } from "@/components/ScheduleSwitcher/ScheduleSwitcher";
import { useFacilityStartEndTime } from "@/features/Facility";
import { useFacilityGroupSize } from "@/features/Facility/hooks/useFacilityGroupSize";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { VStack } from "@whoosh/design-system/src/components/primitive";

import {
  TimeOfDayFilterControl,
  GroupSizeFilterControl,
} from "../../../../components/AgendaFilters";
import {
  agendaQueryInputs_courseBucketIds,
  agendaQueryInputs_duration,
  agendaQueryInputs_groupSize,
  agendaQueryInputs_times,
} from "../../atoms/agendaQueryInputs";
import {
  CourseBucketFilterControl,
  DateSelectorControl,
  DurationFilterControl,
} from "../DurationBasedFilters";
import { DurationBasedAgendaSidebarFragment_facility$key } from "./__generated__/DurationBasedAgendaSidebarFragment_facility.graphql";
import { DurationBasedAgendaSidebar_session$key } from "./__generated__/DurationBasedAgendaSidebar_session.graphql";

type Props = {
  facilityRef: DurationBasedAgendaSidebarFragment_facility$key | null;
  sessionRef: DurationBasedAgendaSidebar_session$key | null;
};

export const DurationBasedAgendaSidebar = ({
  facilityRef,
  sessionRef,
}: Props) => {
  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  const date = route?.params?.date;

  const session = useFragment(
    DurationBasedAgendaSidebarSessionFragmentString,
    sessionRef
  );
  const facility = useFragment(
    DurationBasedAgendaSidebarFragmentString,
    facilityRef
  );

  const [duration, setDuration] = useAtom(agendaQueryInputs_duration);

  const onDurationChange = (value: number | undefined) => {
    setDuration(value);
    if (!isNil(value) && facility?.id) {
      setFiltersFromStorage(
        `x-whoosh-${facility.id}-duration-filter`,
        value.toString()
      );
    }
  };

  const [groupSize, setGroupSize] = useAtom(agendaQueryInputs_groupSize);

  const [times, setTimes] = useAtom(agendaQueryInputs_times);

  const onGroupSizeChange = (value: number | undefined) => {
    if (!isNil(value) && facility?.id) {
      setGroupSize(value);
      setFiltersFromStorage(
        `x-whoosh-${facility.id}-group-size-filter`,
        value.toString()
      );
    }
  };

  const facilityStartEndTime = useFacilityStartEndTime(facility ?? null);
  const facilityGroupSizes = useFacilityGroupSize(facility ?? null);

  const onTimeOfDayChange = useCallback(
    ({ timeLTE, timeGTE }: { timeLTE?: string; timeGTE?: string }) => {
      if (!timeLTE && !timeGTE) {
        setTimes(undefined);
      } else {
        setTimes({ timeLTE, timeGTE });
      }
    },
    [setTimes]
  );

  const [courseBucketIds, setCourseBucketIds] = useAtom(
    agendaQueryInputs_courseBucketIds
  );

  const onCourseBucketChange = useCallback(
    (value: (string | null)[]) => {
      setCourseBucketIds(value.flatMap((v) => (v ? [v] : [])));
      if (facility?.id) {
        const filteredValues = value.filter((v): v is string => v !== null);
        setFiltersFromStorage(
          `x-whoosh-${facility.id}-course-bucket-filter`,
          filteredValues.length > 0 ? filteredValues.join(",") : ""
        );
      }
    },
    [setCourseBucketIds, facility?.id]
  );

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const curDate = new Date();
    const todayString = format(curDate, "yyyy-MM-dd");
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = facilityToScheduleOption(facility ?? null);

  return (
    <VStack gap={5} px={2}>
      <ScheduleSwitcher
        selectedOption={selectedScheduleOption}
        onChange={onScheduleSwitcherChange}
        sessionRef={session ?? null}
      />
      <DurationFilterControl
        facilityRef={facility ?? null}
        onChange={onDurationChange}
        selectedDuration={duration}
      />
      <TimeOfDayFilterControl
        controlStartTime={facilityStartEndTime.startTime}
        controlEndTime={facilityStartEndTime.endTime}
        onChange={onTimeOfDayChange}
        selectedTimeRange={times}
      />
      <GroupSizeFilterControl
        controlMinGroupSize={facilityGroupSizes.min}
        controlMaxGroupSize={facilityGroupSizes.max}
        onChange={onGroupSizeChange}
        selectedGroupSize={groupSize}
      />
      <CourseBucketFilterControl
        facilityRef={facility ?? null}
        onChange={onCourseBucketChange}
        selectedCourseBucketIds={courseBucketIds}
      />
      <DateSelectorControl facilityRef={facility ?? null} />
    </VStack>
  );
};

export const DurationBasedAgendaSidebarFragmentString = graphql`
  fragment DurationBasedAgendaSidebarFragment_facility on Facility {
    id
    ...useFacilityStartEndTime_facility
    ...useFacilityGroupSize_facility
    ...CourseBucketFilterControlFragment_facility
    ...DurationFilterControlFragment_facility
    ...DateSelectorControlFragment_facility
    ...facilityToScheduleOption_facility
  }
`;

const DurationBasedAgendaSidebarSessionFragmentString = graphql`
  fragment DurationBasedAgendaSidebar_session on Session {
    ...ScheduleSwitcher_session
  }
`;
